import { formatDateTimeSeconds } from '@/lib/utils'
import { borderRadius, colors } from '@/theme'
import type { TimelineEvent } from '@/types/timeline'
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined'
import ExpandMore from '@mui/icons-material/ExpandMore'
import MemoryOutlined from '@mui/icons-material/MemoryOutlined'
import PersonOutline from '@mui/icons-material/PersonOutline'
import { Box, Chip, Collapse, Divider, IconButton, Link, Typography } from '@mui/material'
import { useState } from 'react'
import { CATEGORY_CONFIG } from './config'
import { getNoteTypeConfig } from './noteTypeConfig'
import { parseLink, parseMerge, stripSentByLine } from './noteParser'
import TimelineFieldChanges from './TimelineFieldChanges'

interface TimelineEventCardProps {
  event: TimelineEvent
  patientHash?: string
}

const descriptionTextSx = {
  fontSize: '13px',
  color: colors.text.secondary,
  lineHeight: 1.5,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word'
} as const

function LinkedPatientsDescription({ note, patientHash }: { note: string; patientHash?: string }) {
  const parsed = parseLink(note)
  if (!parsed) return null

  const otherPatients = parsed.linkedPatients.filter(h => h !== patientHash)
  if (otherPatients.length === 0) return null

  return (
    <Typography component='div' sx={descriptionTextSx}>
      Linked patients:{' '}
      {otherPatients.map((hash, i) => (
        <span key={hash}>
          <Link href={`/patients/${hash}`} sx={{ fontSize: '13px', color: colors.brand.primary }}>
            {hash}
          </Link>
          {i < otherPatients.length - 1 && ', '}
        </span>
      ))}
    </Typography>
  )
}

function MergeDescription({ note, patientHash }: { note: string; patientHash?: string }) {
  const parsed = parseMerge(note)
  if (!parsed) return null

  const hash = parsed.lostPatientRecordHash
  if (hash === patientHash) {
    return <Typography sx={descriptionTextSx}>Merged with patient {hash}</Typography>
  }

  return (
    <Typography sx={descriptionTextSx}>
      Merged with patient{' '}
      <Link href={`/patients/${hash}`} sx={{ fontSize: '13px', color: colors.brand.primary }}>
        {hash}
      </Link>
    </Typography>
  )
}

function EmailDescription({ note }: { note: string }) {
  const msgId = note.slice(-24)
  const link = `${import.meta.env.VITE_MESSAGING_API_URL.replace('/api', '')}/logs/?id=${msgId}`
  const displayNote = stripSentByLine(note)

  return (
    <div>
      <Typography sx={descriptionTextSx}>{displayNote}</Typography>
      <Link href={link} target='_blank'>
        {link}
      </Link>
    </div>
  )
}

export default function TimelineEventCard({ event, patientHash }: TimelineEventCardProps) {
  const hasExpandableContent = event.fieldChanges && event.fieldChanges.length > 0
  const [isExpanded, setIsExpanded] = useState(false)
  const categoryConfig = CATEGORY_CONFIG[event.category]

  const noteConfig = getNoteTypeConfig(event.noteType)
  const IconComponent = noteConfig.Icon
  const iconBg = noteConfig.iconBg ?? categoryConfig.iconBg
  const iconColor = noteConfig.iconColor ?? categoryConfig.iconColor

  const renderDescription = () => {
    if (event.noteType === 'Communications') {
      return <EmailDescription note={event.note} />
    }
    if (event.noteType === 'link') {
      return <LinkedPatientsDescription note={event.note} patientHash={patientHash} />
    }
    if (event.noteType === 'merge') {
      return <MergeDescription note={event.note} patientHash={patientHash} />
    }
    if (event.description && event.description !== event.title) {
      return <Typography sx={descriptionTextSx}>{event.description}</Typography>
    }
    return null
  }

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: colors.background.paper,
        border: `1px solid ${colors.border.default}`,
        borderRadius: borderRadius.md,
        p: 2.5,
        pt: 3,
        overflow: 'visible',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -18,
          left: 12,
          zIndex: 1,
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: iconBg,
          color: iconColor,
          borderRadius: '50%',
          border: categoryConfig.chipBorder ? `1px solid ${categoryConfig.chipBorder}` : 'none'
        }}
      >
        <IconComponent sx={{ fontSize: 14 }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Chip
          label={event.category}
          size='small'
          sx={{
            bgcolor: categoryConfig.chipBg,
            color: categoryConfig.chipColor,
            border: categoryConfig.chipBorder ? `1px solid ${categoryConfig.chipBorder}` : 'none',
            fontWeight: 500,
            fontSize: '11px',
            height: '24px'
          }}
        />
        <Divider orientation='vertical' sx={{ my: 0.5 }} flexItem />
        <Typography
          sx={{
            fontSize: '12px',
            ml: 0.5,
            color: event.source === 'System' ? colors.text.muted : colors.text.secondary,
            fontStyle: event.source === 'System' ? 'italic' : 'normal'
          }}
        >
          {event.source}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeOutlined sx={{ fontSize: 14, color: colors.text.muted }} />
          <Typography sx={{ fontSize: '12px', color: colors.text.secondary }}>
            {formatDateTimeSeconds(event.timestamp)}
          </Typography>
        </Box>

        <Box sx={{ width: '1px', height: '14px', bgcolor: colors.border.default, mx: 0.5 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {event.source === 'System' ? (
            <MemoryOutlined sx={{ fontSize: 14, color: colors.text.muted }} />
          ) : (
            <PersonOutline sx={{ fontSize: 14, color: colors.text.muted }} />
          )}
          <Typography
            sx={{
              fontSize: '12px',
              color: event.source === 'System' ? colors.text.muted : colors.text.secondary,
              fontStyle: event.source === 'System' ? 'italic' : 'normal'
            }}
          >
            {event.user}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.text.primary, lineHeight: 1.5 }}>
            {event.title}
          </Typography>
          {renderDescription()}
        </Box>

        {hasExpandableContent && (
          <IconButton
            size='small'
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              color: colors.text.muted,
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              ml: 1
            }}
          >
            <ExpandMore fontSize='small' />
          </IconButton>
        )}
      </Box>

      {hasExpandableContent && (
        <Collapse in={isExpanded}>
          <TimelineFieldChanges changes={event.fieldChanges!} />
        </Collapse>
      )}
    </Box>
  )
}
